import { useState, useCallback, useRef } from 'react';
import { 
  parseFile, 
  analyzeQuestionStructure, 
  transformToSurveyFormat, 
  validateImportData, 
  generatePreviewData,
  estimateMemoryUsage,
  resolveClientAndProgramIds,
  sanitizeResponseData,
  SUPPORTED_FILE_TYPES 
} from '../lib/surveyImport.js';
import { operationsBase, TABLES } from '../lib/airtable.js';

/**
 * React hook for managing external survey imports
 * Provides file parsing, validation, preview, and import functionality
 */
export function useSurveyImport() {
  const [state, setState] = useState({
    // File processing state
    file: null,
    isProcessing: false,
    
    // Parsed data
    parsedData: null,
    questionMapping: null,
    
    // Preview and validation
    previewData: null,
    validationResults: null,
    memoryEstimate: null,
    
    // Import state
    isImporting: false,
    importProgress: 0,
    importResults: null,
    
    // Error handling
    error: null,
    warnings: []
  });

  const abortControllerRef = useRef(null);

  /**
   * Reset the import state
   */
  const reset = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    setState({
      file: null,
      isProcessing: false,
      parsedData: null,
      questionMapping: null,
      previewData: null,
      validationResults: null,
      memoryEstimate: null,
      isImporting: false,
      importProgress: 0,
      importResults: null,
      error: null,
      warnings: []
    });
  }, []);

  /**
   * Process uploaded file and generate preview
   * @param {File} file - The uploaded file
   * @param {Object} options - Processing options
   */
  const processFile = useCallback(async (file, options = {}) => {
    if (!file) {
      setState(prev => ({ ...prev, error: 'No file provided' }));
      return false;
    }

    setState(prev => ({ 
      ...prev, 
      file, 
      isProcessing: true, 
      error: null, 
      warnings: [] 
    }));

    try {
      abortControllerRef.current = new AbortController();

      // Parse the file
      const { data, metadata } = await parseFile(file);
      
      if (abortControllerRef.current.signal.aborted) {
        return false;
      }

      // Analyze question structure
      const questionMapping = analyzeQuestionStructure(data, metadata);
      
      // Generate preview data (first 5 rows)
      const tempSurveyData = transformToSurveyFormat(data, questionMapping, {
        client: options.client || 'Preview Client',
        program: options.program || 'Preview Program',
        associatedSOs: options.associatedSOs || '',
        originalFilename: file.name,
        surveyTitle: options.surveyTitle || 'Preview Survey'
      });
      
      const previewData = generatePreviewData({...tempSurveyData, questionMapping}, 20);
      const memoryEstimate = estimateMemoryUsage(tempSurveyData);

      setState(prev => ({
        ...prev,
        isProcessing: false,
        parsedData: { data, metadata },
        questionMapping,
        previewData,
        memoryEstimate,
        warnings: [...(tempSurveyData.responses[0]?.metadata?.validation_warnings || [])]
      }));

      return true;
    } catch (error) {
      setState(prev => ({
        ...prev,
        isProcessing: false,
        error: error.message
      }));
      return false;
    }
  }, []);

  /**
   * Validate the processed data for import
   * @param {Object} importData - Import data containing client, program, associatedSOs
   * @param {Object} importOptions - Additional import options
   */
  const validateForImport = useCallback((importData, importOptions = {}) => {
    if (!state.parsedData || !state.questionMapping) {
      setState(prev => ({ 
        ...prev, 
        error: 'No processed data available for validation' 
      }));
      return false;
    }

    try {
      // Transform data with actual import parameters
      const surveyData = transformToSurveyFormat(
        state.parsedData.data, 
        state.questionMapping, 
        {
          client: importData.client,
          program: importData.program,
          associatedSOs: importData.associatedSOs,
          originalFilename: state.file.name,
          surveyTitle: importOptions.surveyTitle || 'External Survey',
          sourceType: importOptions.sourceType || 'Client Excel',
          notes: importOptions.notes || '',
          importedBy: importOptions.importedBy || 'Admin'
        }
      );

      // Validate the transformed data
      const validationResults = validateImportData(surveyData, importData);
      
      setState(prev => ({
        ...prev,
        validationResults,
        previewData: generatePreviewData({...surveyData, questionMapping: state.questionMapping}, 10), // Extended preview for validation
        error: validationResults.isValid ? null : validationResults.errors.join('; '),
        warnings: [...prev.warnings, ...validationResults.warnings]
      }));

      return validationResults.isValid;
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: `Validation failed: ${error.message}`
      }));
      return false;
    }
  }, [state.parsedData, state.questionMapping, state.file]);

  /**
   * Execute the import to Airtable
   * @param {Object} importData - Import data containing client, program, associatedSOs
   * @param {Object} importOptions - Import configuration
   */
  const executeImport = useCallback(async (importData, importOptions = {}) => {
    if (!state.parsedData || !state.questionMapping || !state.validationResults?.isValid) {
      setState(prev => ({ 
        ...prev, 
        error: 'Data not ready for import. Please validate first.' 
      }));
      return false;
    }

    setState(prev => ({ 
      ...prev, 
      isImporting: true, 
      importProgress: 0,
      error: null 
    }));

    try {
      abortControllerRef.current = new AbortController();

      // Step 1: Resolve client and program names to record IDs
      setState(prev => ({ ...prev, importProgress: 5 }));
      const { clientId, programId } = await resolveClientAndProgramIds(
        importData.client,
        importData.program
      );

      // Transform data for import
      const surveyData = transformToSurveyFormat(
        state.parsedData.data, 
        state.questionMapping, 
        {
          client: importData.client,
          program: importData.program,
          associatedSOs: importData.associatedSOs,
          originalFilename: state.file.name,
          surveyTitle: importOptions.surveyTitle || 'External Survey',
          sourceType: importOptions.sourceType || 'Client Excel',
          notes: importOptions.notes || '',
          importedBy: importOptions.importedBy || 'Admin'
        }
      );

      // Step 2: Create import record with actual record IDs
      setState(prev => ({ ...prev, importProgress: 10 }));
      
      // Update the import record to use record IDs instead of names
      const importRecordWithIds = {
        ...surveyData.importRecord,
        'Client': [clientId],  // Airtable expects array of record IDs
        'Program': [programId] // Airtable expects array of record IDs
      };
      
      const importRecord = await operationsBase(TABLES.EXTERNAL_SURVEY_IMPORTS).create(
        importRecordWithIds
      );

      if (!importRecord || !importRecord.id) {
        throw new Error('Failed to create import record - server returned invalid response');
      }

      if (abortControllerRef.current.signal.aborted) {
        return false;
      }

      // Step 2: Create SO association records
      setState(prev => ({ ...prev, importProgress: 15 }));
      
      if (importData.associatedSOs && importData.associatedSOs.trim()) {
        const soNumbers = importData.associatedSOs.split(',').map(so => so.trim());
        const soAssociations = soNumbers.map(soNumber => ({
          fields: {
            'Survey Import': [importRecord.id],
            'SO': parseInt(soNumber),
            'Association Notes': `Auto-created during import of ${state.file.name}`
          }
        }));

        try {
          await operationsBase(TABLES.EXTERNAL_SURVEY_SO_ASSOCIATIONS).create(soAssociations);
          // SO associations created successfully
        } catch (error) {
          console.warn('Failed to create SO associations:', error.message);
          // Don't fail the entire import for SO association errors
        }
      }

      if (abortControllerRef.current.signal.aborted) {
        return false;
      }

      // Step 3: Create response records in batches
      const batchSize = 10; // Airtable batch limit
      const responses = surveyData.responses;
      const batches = [];
      
      for (let i = 0; i < responses.length; i += batchSize) {
        batches.push(responses.slice(i, i + batchSize));
      }

      let processedResponses = 0;
      const createdResponses = [];

      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];
        
        // Prepare batch records with comprehensively sanitized JSON
        const batchRecords = batch.map((response, index) => {
          // Apply comprehensive XSS sanitization to the entire response object
          const sanitizedResponse = sanitizeResponseData(response);
          
          return {
            fields: {
              'Response ID': `${importRecord.id}_${processedResponses + index + 1}`,
              'Survey Import': [importRecord.id],
              'Response Data': JSON.stringify(sanitizedResponse),
              'Response Number': processedResponses + index + 1,
              'Response Date': response.metadata.import_date?.split('T')[0] || new Date().toISOString().split('T')[0]
            }
          };
        });

        // Create batch with retry logic
        let batchResults;
        let retryCount = 0;
        const maxRetries = 3;
        
        while (retryCount <= maxRetries) {
          try {
            batchResults = await operationsBase(TABLES.EXTERNAL_SURVEY_RESPONSES).create(
              batchRecords
            );
            
            if (!batchResults || batchResults.length !== batchRecords.length) {
              throw new Error(`Batch returned ${batchResults?.length || 0} results, expected ${batchRecords.length}`);
            }
            
            break; // Success, exit retry loop
            
          } catch (error) {
            retryCount++;
            
            if (retryCount > maxRetries) {
              throw new Error(`Batch ${batchIndex + 1} failed after ${maxRetries} retries: ${error.message}`);
            }
            
            // Exponential backoff: 1s, 2s, 4s
            const delay = Math.pow(2, retryCount) * 1000;
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
        
        createdResponses.push(...batchResults);
        processedResponses += batch.length;

        // Update progress
        const progress = 20 + ((processedResponses / responses.length) * 75);
        setState(prev => ({ ...prev, importProgress: Math.round(progress) }));

        if (abortControllerRef.current.signal.aborted) {
          return false;
        }

        // Rate limiting: Respect Airtable's 5 requests/second limit
        // 1 second delay ensures we stay well under the limit
        if (batchIndex < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      // Complete
      setState(prev => ({ 
        ...prev, 
        isImporting: false,
        importProgress: 100,
        importResults: {
          success: true,
          importRecordId: importRecord.id,
          responsesCreated: createdResponses.length,
          summary: surveyData.summary,
          client: importData.client,
          program: importData.program,
          associatedSOs: importData.associatedSOs
        }
      }));

      return true;
    } catch (error) {
      setState(prev => ({
        ...prev,
        isImporting: false,
        error: `Import failed: ${error.message}`,
        importResults: {
          success: false,
          error: error.message
        }
      }));
      return false;
    }
  }, [state.parsedData, state.questionMapping, state.validationResults, state.file]);

  /**
   * Update question mapping (for manual adjustments)
   * @param {Object} updatedMapping - Updated question mapping
   */
  const updateQuestionMapping = useCallback((updatedMapping) => {
    setState(prev => ({
      ...prev,
      questionMapping: updatedMapping,
      // Clear validation results as they may be outdated
      validationResults: null,
      previewData: null
    }));
  }, []);

  /**
   * Check if file type is supported
   * @param {File} file - File to check
   * @returns {boolean} Whether file type is supported
   */
  const isFileTypeSupported = useCallback((file) => {
    if (!file) return false;
    const extension = file.name.split('.').pop().toLowerCase();
    return SUPPORTED_FILE_TYPES.ALL.includes(extension);
  }, []);

  /**
   * Cancel ongoing operations
   */
  const cancelOperation = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    setState(prev => ({
      ...prev,
      isProcessing: false,
      isImporting: false,
      error: 'Operation cancelled by user'
    }));
  }, []);

  /**
   * Get file type validation message
   * @param {File} file - File to validate
   * @returns {string|null} Error message or null if valid
   */
  const getFileValidationMessage = useCallback((file) => {
    if (!file) {
      return 'Please select a file';
    }

    const extension = file.name.split('.').pop().toLowerCase();
    if (!SUPPORTED_FILE_TYPES.ALL.includes(extension)) {
      return `Unsupported file type: ${extension}. Supported types: ${SUPPORTED_FILE_TYPES.ALL.join(', ')}`;
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      const sizeMB = Math.round(file.size / 1024 / 1024);
      return `File size (${sizeMB}MB) exceeds limit of 10MB`;
    }

    return null;
  }, []);

  return {
    // State
    ...state,
    
    // Actions
    processFile,
    validateForImport,
    executeImport,
    updateQuestionMapping,
    reset,
    cancelOperation,
    
    // Utilities
    isFileTypeSupported,
    getFileValidationMessage,
    
    // Constants
    SUPPORTED_FILE_TYPES
  };
}